import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Clock, CheckCircle, XCircle, Truck, User, ShoppingBag, Menu, X, ArrowLeft } from 'lucide-react';
import { OrdersService } from '@/services/firebase.service';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CartDrawer from '@/components/CartDrawer';

const MyOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);
  const { currentUser, logout } = useAuth();
  const { getCartCount, setIsCartOpen } = useCart();
  const navigate = useNavigate();
  const cartCount = getCartCount();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [currentUser]);

  const fetchOrders = async () => {
    try {
      const allOrders = await OrdersService.getAll();
      const userOrders = allOrders.filter(order => order.userId === currentUser.uid);
      userOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setOrders(userOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    setClickCount((prev) => prev + 1);
    if (clickTimer) clearTimeout(clickTimer);
    const timer = setTimeout(() => setClickCount(0), 1000);
    setClickTimer(timer);
    if (clickCount + 1 === 3) {
      navigate('/admin/login');
      setClickCount(0);
    }
  };

  const handleCartClick = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setIsCartOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'shipped':
        return <Truck className="w-6 h-6 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'pending':
      case 'processing':
        return <Clock className="w-6 h-6 text-yellow-500" />;
      default:
        return <Package className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]">
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
            <div className="flex items-center justify-between">
              <div className="hidden md:flex items-center space-x-8 w-32"></div>
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <h1 className="text-lg sm:text-xl font-light tracking-[0.2em] text-black">B&C Store</h1>
              </div>
              <div className="flex items-center space-x-4 sm:space-x-5">
                <button className="text-gray-600 hover:text-black transition-colors"><User size={20} strokeWidth={1.5} /></button>
                <button className="text-gray-600 hover:text-black transition-colors relative"><ShoppingBag size={20} strokeWidth={1.5} /></button>
              </div>
            </div>
          </div>
        </nav>
        <div className="pt-24 flex items-center justify-center min-h-screen">
          <div className="w-12 h-12 border border-gray-300 border-t-black rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <CartDrawer />

      {/* Navbar - White/Cream theme matching the site */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="hidden md:flex items-center space-x-8 w-32"></div>

            <button className="md:hidden text-black" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={handleLogoClick}>
              <img 
                src="https://customer-assets.emergentagent.com/job_luxury-estore-3/artifacts/czobo02e_B%26C%20LOGO%20-%2001.png" 
                alt="B&C Store" 
                className="h-10 sm:h-12 object-contain hover:opacity-80 transition-opacity"
              />
            </div>

            <div className="flex items-center space-x-4 sm:space-x-5">
              {currentUser ? (
                <div className="relative">
                  <button onClick={() => setShowUserMenu(!showUserMenu)} className="text-gray-600 hover:text-black transition-colors">
                    <User size={20} strokeWidth={1.5} />
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                      <button onClick={() => { navigate('/profile'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Profile</button>
                      <button onClick={() => { navigate('/my-orders'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Orders</button>
                      <button onClick={async () => { await logout(); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 font-light">Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => navigate('/login')} className="text-gray-600 hover:text-black transition-colors">
                  <User size={20} strokeWidth={1.5} />
                </button>
              )}
              
              <button onClick={handleCartClick} className="text-gray-600 hover:text-black transition-colors relative">
                <ShoppingBag size={20} strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-light">{cartCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">Home</button>
              <button onClick={() => { navigate('/products'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">Products</button>
              <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">My Profile</button>
            </div>
          </motion.div>
        )}
      </nav>
      
      <div className="pt-20 sm:pt-24 pb-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/products')}
            className="flex items-center space-x-2 text-gray-500 hover:text-black mb-6 sm:mb-8 transition-colors font-light text-sm"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
            <span>Back to Products</span>
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-wide">My Orders</h1>
          </motion.div>

          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <Package className="w-20 h-20 mx-auto mb-4 text-gray-400" />
              <h3 className="text-2xl font-light mb-2 text-gray-800">No Orders Yet</h3>
              <p className="text-gray-500 mb-6 font-light">
                Start shopping to see your orders here
              </p>
              <button
                onClick={() => navigate('/products')}
                className="px-8 py-3 bg-black text-white text-sm font-light tracking-widest hover:bg-gray-900 transition-colors"
              >
                Start Shopping
              </button>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {orders.map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-100"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3 mb-3 sm:mb-0">
                      {getStatusIcon(order.orderStatus)}
                      <div>
                        <h3 className="font-medium text-lg text-black">
                          Order #{order.orderId}
                        </h3>
                        <p className="text-sm text-gray-500 font-light">
                          {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-4 py-2 rounded-full text-sm font-light border ${getStatusColor(
                          order.orderStatus
                        )}`}
                      >
                        {order.orderStatus || 'Pending'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500 font-light mb-1">Total Amount</p>
                      <p className="text-2xl font-medium text-black">₹{order.total}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-light mb-1">Delivery Address</p>
                      <p className="text-sm font-light">
                        {order.customer?.address}, {order.customer?.city}
                      </p>
                      <p className="text-sm text-gray-500 font-light">
                        {order.customer?.pincode}
                      </p>
                    </div>
                  </div>

                  {/* Tracking ID */}
                  {order.trackingId && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800 font-light">
                        <span className="font-medium">📦 Tracking ID:</span> {order.trackingId}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-gray-500 font-light mb-3">Items ({order.items?.length})</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(order.items || []).slice(0, 4).map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 bg-gray-50 rounded-lg p-3"
                        >
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-14 h-14 rounded-lg object-cover"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-light text-sm truncate">{item.title}</p>
                            <p className="text-xs text-gray-500 font-light">Qty: {item.quantity}</p>
                            <p className="text-sm font-medium text-black">₹{item.price}</p>
                          </div>
                        </div>
                      ))}
                      {order.items?.length > 4 && (
                        <div className="flex items-center justify-center bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-500 font-light">
                            +{order.items.length - 4} more items
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyOrdersPage;
